import Phaser from 'phaser';
import { BackToFloorPill } from '../../components/BackToFloorPill.js';
import { HelpToggle } from '../../components/HelpToggle.js';
import { NarrationCard } from '../../components/NarrationCard.js';
import { ZoneHelpCard } from '../../components/ZoneHelpCard.js';
import { NON_ZONE_HELP, ZONE_META } from '../../layout/zones.js';
import type { ZoneDef } from '../../layout/zones.js';
import { track } from '../../analytics.js';
import type { CrapsTutorialScene } from './TutorialScene.js';
import type { TutorialDriver } from './tutorialDriver.js';
import type { TutorialStep } from './tutorialStep.js';

export class CrapsTutorialUIScene extends Phaser.Scene {
  private dpr = 1;
  private narration!: NarrationCard;
  private driver: TutorialDriver | undefined;
  private unsubscribeStep: (() => void) | undefined = undefined;
  private helpToggle!: HelpToggle;
  private helpCard: ZoneHelpCard | undefined = undefined;
  private helpDismissCatcher: Phaser.GameObjects.GameObject | undefined = undefined;

  constructor() {
    super('CrapsTutorialUIScene');
  }

  init() {
    this.narration = undefined as unknown as NarrationCard;
    this.driver = undefined;
    this.unsubscribeStep = undefined;
    this.helpToggle = undefined as unknown as HelpToggle;
    this.helpCard = undefined;
    this.helpDismissCatcher = undefined;
  }

  create() {
    const dpr = (this.game.registry.get('dpr') as number | undefined) ?? 1;
    this.dpr = dpr;
    const W = this.scale.width;
    const H = this.scale.height;

    new BackToFloorPill({
      scene: this,
      x: Math.round(12 * dpr),
      y: Math.round(14 * dpr),
      scale: dpr,
      onClick: () => this.game.events.emit('navigate:lobby'),
    });

    this.helpToggle = new HelpToggle({
      scene: this,
      x: Math.round(12 * dpr),
      y: Math.round(46 * dpr),
      scale: dpr,
      onToggle: () => {
        const armed = this.helpToggle.isArmed();
        const table = this.scene.get('CrapsTutorialScene') as CrapsTutorialScene;
        table.setHelpMode(armed);
        // Pause the tutorial UI: hide narration + highlights while help mode is armed.
        this.narration.setVisible(!armed);
        table.highlight.setVisible(!armed);
        if (!armed) this.closeHelpCard();
      },
    });

    const cardW = W;
    const cardH = Math.round(H * 0.22);
    const cardCx = W / 2;
    const cardCy = H - cardH / 2;

    this.narration = new NarrationCard({
      scene: this,
      cx: cardCx,
      cy: cardCy,
      width: cardW,
      height: cardH,
      dpr,
      onBack: () => this.driver?.back(),
      onNext: () => this.driver?.attemptNext(),
      onSkip: () => this.game.events.emit('navigate:lobby'),
      onFinish: () => this.game.events.emit('navigate:lobby'),
    });
  }

  /** Called by main.ts after the bridge is wired. */
  attachDriver(driver: TutorialDriver) {
    this.driver = driver;
    this.unsubscribeStep?.();
    this.unsubscribeStep = driver.onStepChange((step, index) => {
      // Listener fires AFTER the index advances. The "completed" step is
      // therefore index - 1 on the first transition and onward.
      if (index > 0) {
        track('tutorial_step_completed', {
          game: 'craps',
          topic: 'craps-standard',
          step: index - 1,
        });
      }
      this.renderStep(step, index);
    });
    this.renderStep(driver.current(), driver.currentIndex());
  }

  /** Called by tutorialBridge when a required tap is satisfied. */
  notifyRequireSatisfied() {
    if (!this.driver) return;
    this.renderStep(this.driver.current(), this.driver.currentIndex());
  }

  private renderStep(step: TutorialStep, index: number) {
    if (!this.driver) return;
    const total = this.driver.totalSteps();
    const isLast = index >= total - 1;
    const requireUnmet = !!step.require && !this.driver.isRequirementMet();
    const hint = requireUnmet ? this.requirementHintText(step) : undefined;
    this.narration.setStep({
      index,
      total,
      title: step.title,
      body: step.narration,
      backEnabled: index > 0,
      nextEnabled: !requireUnmet && !isLast,
      requirementHint: hint,
      finish: isLast,
    });
    const anchor = this.resolveAnchor(step);
    const W = this.scale.width;
    const H = this.scale.height;
    const cardH = Math.round(H * 0.22);
    if (anchor === 'top') {
      const cy = Math.round(56 * this.dpr) + cardH / 2;
      this.narration.setCenter(W / 2, cy);
    } else {
      this.narration.setCenter(W / 2, H - cardH / 2);
    }
    this.applyHighlights(step);
    this.applyHinting(step);
  }

  private requirementHintText(step: TutorialStep): string {
    if (!step.require) return '';
    if (step.require.kind === 'tapZone' && step.require.id === 'pass') {
      return 'Tap the pass line to continue';
    }
    if (step.require.kind === 'tapDice') return 'Tap the dice to roll';
    if (step.require.kind === 'removeBet') {
      return 'Long-press the chip (or right-click)';
    }
    return '';
  }

  private resolveAnchor(step: TutorialStep): 'top' | 'bottom' {
    if (step.narrationAnchor) return step.narrationAnchor;
    const hl = step.highlight ?? [];
    for (const h of hl) {
      // Anything that lives in the lower half of the canvas (pass line,
      // don't-pass bar, dice) wants the card at the top so the highlighted
      // target stays visible.
      if (h.kind === 'pass') return 'top';
      if (h.kind === 'dice') return 'top';
      if (h.kind === 'zone' && h.id === 'dontPass') return 'top';
    }
    return 'bottom';
  }

  private applyHighlights(step: TutorialStep) {
    const table = this.scene.get('CrapsTutorialScene') as CrapsTutorialScene;
    const polygons: number[][] = [];
    const hl = step.highlight ?? [];
    for (const h of hl) {
      if (h.kind === 'zone' || h.kind === 'pass') {
        const id = h.kind === 'pass' ? 'pass' : h.id;
        const zone = table.zones.find((z) => z.def.id === id);
        if (zone) polygons.push([...zone.def.polygon]);
      } else if (h.kind === 'atsZone') {
        // ATS zones are SVG-less; their cell rect IS the visible content
        // (the layout sized cardW = atsContentW). Convert the rect to a
        // 4-corner polygon so the highlight overlay wraps the whole tile.
        const zone = table.atsZones.find((z) => z.def.id === h.id);
        if (zone) {
          const r = zone.rect;
          polygons.push([
            r.x,         r.y,
            r.x + r.w,   r.y,
            r.x + r.w,   r.y + r.h,
            r.x,         r.y + r.h,
          ]);
        }
      } else if (h.kind === 'puck') {
        const r = 28 * this.dpr;
        const cx = table.puck.x;
        const cy = table.puck.y;
        polygons.push(circlePolygon(cx, cy, r, 24));
      } else if (h.kind === 'dice') {
        const d1 = table.dice1;
        const d2 = table.dice2;
        const cx = (d1.x + d2.x) / 2;
        const cy = (d1.y + d2.y) / 2;
        // Wrap both dice with breathing room — radius is half the centre
        // separation plus a die's half-diagonal (so a tilted/tumbling die
        // still fits) plus a small padding.
        const halfSep = Math.abs(d2.x - d1.x) / 2;
        const dieHalfDiagonal = (d1.width / 2) * Math.SQRT2;
        const r = halfSep + dieHalfDiagonal + 12 * this.dpr;
        polygons.push(circlePolygon(cx, cy, r, 32));
      } else if (h.kind === 'helpPill') {
        // ? pill is on this UIScene at top-left; produce a small rectangle.
        const x = Math.round(12 * this.dpr);
        const y = Math.round(46 * this.dpr);
        const w = Math.round(36 * this.dpr);
        const h2 = Math.round(28 * this.dpr);
        polygons.push([x, y, x + w, y, x + w, y + h2, x, y + h2]);
      }
    }
    table.highlight.setHighlights(polygons);
    const H = this.scale.height;
    const cardH = Math.round(H * 0.22);
    const anchor = this.resolveAnchor(step);
    if (anchor === 'top') {
      table.highlight.setSafeTopY(Math.round(56 * this.dpr) + cardH);
    } else {
      table.highlight.setSafeBottomY(H - cardH);
    }
  }

  private applyHinting(step: TutorialStep) {
    const table = this.scene.get('CrapsTutorialScene') as CrapsTutorialScene;
    // Stop any active hints first.
    for (const z of table.zones) z.stopHinting();
    table.dice1.stopHinting();
    table.dice2.stopHinting();
    if (!step.require) return;
    if (this.driver?.isRequirementMet()) return;
    if (step.require.kind === 'tapZone' || step.require.kind === 'removeBet') {
      const req = step.require;
      const zone = table.zones.find((z) => z.def.id === req.id);
      zone?.startHinting();
    }
    // tapDice steps already pulse via the HighlightOverlay's gold ring
    // around both dice — no per-die halo needed.
  }

  getBankrollAnchor(): { x: number; y: number } {
    return { x: Math.round(40 * this.dpr), y: Math.round(28 * this.dpr) };
  }

  showZoneHelp(def: ZoneDef) {
    const meta = ZONE_META[def.id];
    if (!meta) return;
    this.openHelpCardWith(meta.helpTitle, meta.helpText);
  }

  showNonZoneHelp(key: keyof typeof NON_ZONE_HELP) {
    const entry = NON_ZONE_HELP[key];
    this.openHelpCardWith(entry.helpTitle, entry.helpText);
  }

  private openHelpCardWith(title: string, body: string) {
    const W = this.scale.width;
    const H = this.scale.height;
    const cardW = Math.min(Math.round(W * 0.86), Math.round(420 * this.dpr));
    if (!this.helpCard) {
      this.helpCard = new ZoneHelpCard({
        scene: this,
        cx: W / 2,
        cy: H / 2,
        width: cardW,
        dpr: this.dpr,
        onClose: () => this.closeHelpCard(),
      });
      this.helpCard.setDepth(950);
      this.helpDismissCatcher = this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0)
        .setInteractive()
        .setDepth(940);
      this.helpDismissCatcher.on('pointerdown', () => this.closeHelpCard());
    }
    this.helpCard.setContent(title, body);
  }

  private closeHelpCard() {
    this.helpCard?.destroy();
    this.helpCard = undefined;
    this.helpDismissCatcher?.destroy();
    this.helpDismissCatcher = undefined;
  }
}

function circlePolygon(cx: number, cy: number, r: number, segs: number): number[] {
  const pts: number[] = [];
  for (let i = 0; i < segs; i++) {
    const a = (i / segs) * Math.PI * 2;
    pts.push(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
  }
  return pts;
}
